// Removed import from @shared/documents
import { useEffect, useRef, useState } from 'react';

import { useAuth } from '../../hooks/useAuth';
import useFetch from '../../hooks/useFetch';
import { useStickyFlip } from '../../hooks/useStickyFlip'; // Import custom hook
import { extractErrorMessage } from '../../utils/errorHandling';
import { useNavHeight } from '../Context/NavHeightContext'; // Import nav height context
import {
	ContentWrapper, // Import shared components
	ErrorMessage,
	LoadingGif,
	LoadingMessage,
	PageTitle,
	// SubSectionTitle removed as it's unused
	StickyFlipPaginationContainer,
	WideContentContainer,
} from '../Shared/CommonStyles'; // Import shared styles
import Pagination from '../Shared/Pagination';

import * as S from './Posts.styles';
import PostSummary from './PostSummary';

// Define the types locally
interface PostElementDoc {
	type: string;
	content: string;
}

interface PostDocument {
	_id: string;
	title: string;
	elements: PostElementDoc[];
	upload_date: Date | string;
	data_actualitzacio: Date | string;
	unitat?: string;
	url: string;
	data_publicacio_dd_mm_yyyy?: string;
	data_actualitzacio_dd_mm_yyyy?: string;
	preview_image: number;
}

interface UnitatDocument {
	_id: string;
	nom: string;
	nom_lowercase: string;
	abreviacio: string;
	abreviacio_lowercase: string;
	descripcio: string;
	user?: string;
	url?: string;
}

interface PostList {
	posts: PostDocument[];
	pageCount: number;
}

const Posts = ({ unitat: propUnitat }: { unitat?: string }) => {
	const { user } = useAuth();
	const { navHeight } = useNavHeight();
	const [sortField, setSortField] = useState('upload_date');
	const [sortDirection, setSortDirection] = useState('desc');
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(4);
	const [seed, setSeed] = useState(Math.random());

	const unitatToFetch = propUnitat;

	// The create-post backend matrix allows Admin/Agrupament anywhere and
	// Unitat-role users for their own unitat — Familiar/Caps/Tresoreria can
	// never create posts, so hide the CTA for them (was: any logged-in user).
	// Admin/Agrupament anywhere, Caps anywhere (trusted curators), Unitat
	// role for their own unitat. Familiar/Tresoreria hidden.
	const canCreatePost =
		!!user &&
		(user.roles.includes('Admin') ||
			user.roles.includes('Agrupament') ||
			user.roles.includes('Caps') ||
			user.roles.includes('Unitat'));

	const {
		data: postList,
		isLoading: isFetchingPosts,
		error: fetchPostsError,
	} = useFetch<PostList>({
		queryKey: [
			'posts',
			unitatToFetch ?? 'tots', // Simplified key
			sortField,
			sortDirection,
			page,
			pageSize,
			seed,
		],
		url: `/api/posts?sortField=${sortField}&sortDirection=${sortDirection}&page=${page}&size=${pageSize}&seed=${seed}&unitat=${
			unitatToFetch ?? 'tots' // Simplified URL param
		}`,
	});

	const {
		isLoading: isFetchingUnitat,
		error: fetchUnitatError,
	} = useFetch<UnitatDocument>({
		queryKey: ['unitat', propUnitat],
		url: propUnitat ? `/api/unitats/${propUnitat}` : '',
	});

	// Removed handleUnitatChange

	const handleSortFieldChange = (newField: string) => {
		if (newField === 'random') {
			const newSeed = Math.random();
			setSeed(newSeed);
			localStorage.setItem('posts_seed', newSeed.toString());
		}
		setSortField(newField);
	};

	useEffect(() => {
		const seedFromLocalStorage = localStorage.getItem('posts_seed');
		if (seedFromLocalStorage) {
			setSeed(parseFloat(seedFromLocalStorage));
		}
	}, []);

	useEffect(() => {
		setPage(1);
	}, [sortField, sortDirection, pageSize, unitatToFetch, seed]); // Added unitatToFetch dependency

	// --- Pagination Jump/FLIP Logic ---
	const contentWrapperRef = useRef<HTMLDivElement>(null);
	const paginationRef = useRef<HTMLDivElement>(null);

	const { isBottom, isStuck } = useStickyFlip({
		contentWrapperRef,
		stickyContainerRef: paginationRef,
        stickyTop: navHeight + 10,
	});

	const loading = isFetchingPosts || isFetchingUnitat;
	const error = fetchPostsError || fetchUnitatError;

	if (loading) {
		return (
			<WideContentContainer>
				<LoadingMessage>
					<LoadingGif size="large" />
				</LoadingMessage>
			</WideContentContainer>
		);
	}

	if (error) {
		return (
			<WideContentContainer>
				<ErrorMessage>Error: {extractErrorMessage(error)}</ErrorMessage>
			</WideContentContainer>
		);
	}

	if (!postList) {
		return (
			<WideContentContainer>
				<ErrorMessage>
					No s&apos;han pogut carregar els posts
				</ErrorMessage>
			</WideContentContainer>
		);
	}

	return postList ? (
		<WideContentContainer>
			{/* Title moved out of ControlsContainer */}
			<div style={{ width: '100%', marginBottom: 32 }}>
				<PageTitle style={{ marginBottom: 0 }}>Publicacions</PageTitle>
			</div>

			{/* Sticky Pagination */}
			<StickyFlipPaginationContainer
				$navHeight={navHeight}
				$isBottom={isBottom}
                $isStuck={isStuck}
				ref={paginationRef}
			>
				<Pagination
					currentPage={page}
					pageCount={postList.pageCount}
					pageSize={pageSize}
					sortField={sortField}
					sortDirection={sortDirection}
					sortFieldOptions={[
						{
							value: 'upload_date',
							label: 'Data de publicació',
						},
						{
							value: 'data_actualitzacio',
							label: "Data d'actualització",
						},
						{ value: 'title', label: 'Títol' },
						{ value: 'random', label: 'Aleatori' },
					]}
					onPageChange={setPage}
					onPageSizeChange={(newSize) => {
						setPageSize(newSize);
						setPage(1);
					}}
					onSortFieldChange={handleSortFieldChange}
					onSortDirectionChange={setSortDirection}
					pageSizeOptions={[4, 8, 12]}
					showSort={false} // Hide sort controls for Posts page
				/>
			</StickyFlipPaginationContainer>

			{/* Content Wrapper for Scroll Detection */}
			<ContentWrapper ref={contentWrapperRef}>
				<S.PostsGrid>
					{postList.posts.length === 0 ? (
						<S.NoPostsMessage>No hi ha posts!</S.NoPostsMessage>
					) : (
						postList.posts.map((post) => (
							<PostSummary key={post._id} postData={post} />
						))
					)}
				</S.PostsGrid>
			</ContentWrapper>

			{canCreatePost && (
				<S.CreatePostButton
					to={`/posts/crear${propUnitat ? `/${propUnitat}` : ''}`}
				>
					Nou post
				</S.CreatePostButton>
			)}
		</WideContentContainer>
	) : (
		<WideContentContainer>
			<ErrorMessage>No s&apos;han pogut carregar els posts</ErrorMessage>
		</WideContentContainer>
	);
};

export default Posts;
